import type { FC } from "react"
import { useMemo, useState } from "react"
import { Box, Flex, HStack, Icon, Link, Stack, Text } from "@chakra-ui/react"
import { AccountId as CaipAccountId } from "caip"
import { formatUnits } from "@ethersproject/units"
import { isAddress } from "@ethersproject/address"
import { formatEther } from "ethers/lib/utils"
import invariant from "tiny-invariant"

import { ROUTES } from "common/constants/routes"
import type {
  AccountId,
  GovernorContract,
  Maybe,
  ExecutableCall,
} from "query/graphql"
import {
  useProposalTransferTokensReceiptQuery,
  ExecutableCallType,
} from "query/graphql"
import { getChainIdParams } from "web3/helpers/chainId"
import {
  accountIdToChainId,
  addressToAccountId,
} from "web3/helpers/transformers"
import { useParseProposalAction } from "proposal/hooks/useParseProposalAction"
import ArrowRight from "ui/components/icons/ArrowRight"
import { labelNumber } from "common/helpers/number"
import { useTokenBalances } from "common/hooks/useTokenBalances"
import { getChain } from "web3/helpers/chains"
import { getCustomProposalSourceTreasuryAddress } from "governance/helpers/treasury"
import { shortenIfAddress } from "web3/helpers/address"
import ExternalLink from "ui/components/icons/ExternalLink"
import { useDevice } from "common/hooks/useDevice"
import { subString } from "common/helpers/string"
import { ArrowUpRight } from "ui/components/icons/font-awesome/ArrowUpRight"

type ProposalTransferReceiptProps = {
  executableCall: ExecutableCall
  chainId: string
  governance: {
    slug: string
    contracts: {
      governor: Pick<GovernorContract, "type" | "address">
    }
    timelockId?: Maybe<AccountId>
  }
  governanceId: AccountId
}

const ProposalTransferReceipt: FC<ProposalTransferReceiptProps> = ({
  executableCall,
  chainId,
  governance,
  governanceId,
}) => {
  const [shouldDisplayValues, setShouldDisplayValues] = useState(false)
  const { onLargeDevice, onLittleDevice } = useDevice()

  const { signature, calldata, type, target, value } = executableCall

  const { reference } = getChainIdParams(chainId)

  const { decodedCalldata, status: decodedStatus } = useParseProposalAction({
    chainId,
    calldata: calldata || "",
    target,
    signature: signature || "",
  })

  const recipientAddress = useMemo(() => {
    if (type === ExecutableCallType.Nativetransfer) {
      return target
    }

    if (type === ExecutableCallType.Erc20transferarbitrum) {
      // the second parameter of the Arbitrum transfer method is the recipient address
      const recipientAddress = decodedCalldata?.[1]?.value

      if (!recipientAddress) return ""

      return recipientAddress
    }

    // the first parameter of the transfer method is the recipient address
    const recipientAddress = decodedCalldata?.[0]?.value

    if (!recipientAddress) return ""

    return recipientAddress
  }, [type, target, decodedCalldata])

  const treasuryAddress = useMemo(() => {
    if (!governance) return ""

    const { timelockId } = governance
    const { governor } = governance.contracts

    const treasuryAddress = timelockId
      ? CaipAccountId.parse(timelockId).address
      : governor.address

    return treasuryAddress
  }, [governance])

  const treasuryAccountId = addressToAccountId(
    treasuryAddress,
    reference as number,
  )
  const recipientAccountId = addressToAccountId(
    recipientAddress,
    reference as number,
  )

  const accountIds =
    Boolean(recipientAddress) && Boolean(treasuryAccountId)
      ? [recipientAccountId, treasuryAccountId]
      : null

  const { data: queryData, isLoading } = useProposalTransferTokensReceiptQuery(
    {
      accountIds,
    },
    { enabled: Boolean(accountIds) },
  )

  const governanceChainId = accountIdToChainId(governanceId)
  const chain = getChain(governanceChainId)

  const { reference: governanceChainReference } =
    getChainIdParams(governanceChainId)

  const { tokenBalances } = useTokenBalances(
    governanceChainReference,
    type === ExecutableCallType.Erc20transferarbitrum
      ? (getCustomProposalSourceTreasuryAddress({ slug: "arbitrum" }) as string)
      : treasuryAddress,
  )

  const token = useMemo(() => {
    if (type === ExecutableCallType.Nativetransfer) return

    if (type === ExecutableCallType.Erc20transferarbitrum && decodedCalldata) {
      const tokenAddress = decodedCalldata[0].value

      return tokenBalances?.tokens?.find(
        (token) =>
          token.tokenInfo.address?.toLowerCase() === tokenAddress.toLowerCase(),
      )
    }

    return tokenBalances?.tokens?.find(
      (token) =>
        token.tokenInfo.address?.toLowerCase() === target.toLowerCase(),
    )
  }, [decodedCalldata, type, target, tokenBalances])

  const tokenLabel = useMemo(() => {
    if (!token && governance?.slug.includes("arbitrum")) {
      if (type === ExecutableCallType.Nativetransfer) {
        return "ETH"
      }

      return "ARB"
    }

    if (!token) return chain.nativeCurrency.symbol

    return token.tokenInfo.symbol
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, chain])

  const tokenPrice = useMemo(() => {
    if (!token) return tokenBalances?.nativeAsset.tokenInfo.price.rate

    return token.tokenInfo.price.rate
  }, [token, tokenBalances])

  const tokenValue = useMemo(() => {
    if (type === ExecutableCallType.Nativetransfer) {
      invariant(value, '"value" should exist in a transferNativeAsset')
      setShouldDisplayValues(true)

      return Number(formatEther(value))
    }

    if (type === ExecutableCallType.Erc20transferarbitrum && decodedCalldata) {
      const valueString = formatUnits(
        decodedCalldata[2].value,
        token?.tokenInfo?.decimals,
      )

      setShouldDisplayValues(true)

      return Number(valueString)
    }

    if (type === ExecutableCallType.Erc20transfer && decodedCalldata) {
      const valueString = formatUnits(
        decodedCalldata[1].value,
        token?.tokenInfo?.decimals,
      )

      setShouldDisplayValues(true)

      return Number(valueString)
    }

    return 0
  }, [token, value, type, decodedCalldata])

  const targetAccount = queryData?.accounts.find(
    (account) => account.address === recipientAddress,
  )

  const recipientLabel = useMemo(() => {
    if (targetAccount?.name && !isAddress(targetAccount?.name))
      return targetAccount?.name

    return undefined
  }, [targetAccount])

  if (!queryData || isLoading || decodedStatus?.success === false) return null

  if (!shouldDisplayValues) {
    return null
  }

  return (
    <Stack bg="white" w="full">
      <Flex align="center" display={onLargeDevice} p={5}>
        <HStack align="center" spacing={3} w="25%">
          <Icon as={ArrowRight} fill="#344054" h={4} w={4} />
          <Text
            color="#344054"
            fontSize="14px"
            fontWeight={500}
            lineHeight="20px"
          >
            Transfer
          </Text>
        </HStack>
        <Stack
          direction="row"
          overflow="hidden"
          spacing={2}
          textOverflow="ellipsis"
          w="30%"
          whiteSpace="nowrap"
        >
          <Stack justify="center" spacing={0}>
            {recipientLabel ? (
              <Text
                color="#000000"
                fontSize="14px"
                fontWeight="medium"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {subString(recipientLabel, 28)}
              </Text>
            ) : null}
            <HStack p={0}>
              {!isAddress(targetAccount?.name ? targetAccount.name : "") && (
                <Text color="gray.500" fontSize="14px" fontWeight="medium">
                  {shortenIfAddress(targetAccount?.address)}
                </Text>
              )}
              <Link isExternal href={ROUTES.profile(targetAccount?.address)}>
                <Icon
                  as={ArrowUpRight}
                  color="#98A2B3"
                  h={2.5}
                  mb={0.5}
                  w={2.5}
                />
              </Link>
            </HStack>
          </Stack>
        </Stack>
        <Box borderRadius="md" maxW="30%" py={1}>
          <Text
            color="gray.700"
            fontSize="14px"
            fontWeight="bold"
            overflow="hidden"
            textAlign="center"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {tokenPrice ? (
              <>
                {`${labelNumber(tokenValue)} ${tokenLabel} 
              ($${labelNumber(tokenValue * tokenPrice)} USD)`}
              </>
            ) : (
              `${labelNumber(tokenValue)} ${tokenLabel}`
            )}
          </Text>
        </Box>
      </Flex>
      <Stack display={onLittleDevice} px={5} py={2} spacing={0}>
        <HStack align="center" py={2}>
          <Icon as={ArrowRight} fill="#344054" h={3} w={3} />
          <Text
            color="#344054"
            fontSize="14px"
            fontWeight={400}
            lineHeight="20px"
          >
            Transfer
          </Text>
        </HStack>
        <HStack
          justify="space-between"
          overflow="hidden"
          py={2}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          <Text color="#667085" textStyle="body.regular.md">
            Address
          </Text>
          <HStack spacing={2}>
            <Stack p={0} spacing={0}>
              {recipientLabel ? (
                <Text textStyle="body.bold.md">
                  {subString(recipientLabel, 16)}
                </Text>
              ) : null}
              <HStack p={0}>
                {!isAddress(targetAccount?.name ? targetAccount.name : "") && (
                  <Text color="#667085" textStyle="body.regular.md">
                    {shortenIfAddress(targetAccount?.address)}
                  </Text>
                )}
                <Link isExternal href={ROUTES.profile(targetAccount?.address)}>
                  <Icon
                    as={ExternalLink}
                    color="#98A2B3"
                    h={2.5}
                    mb={0.5}
                    w={2.5}
                  />
                </Link>
              </HStack>
            </Stack>
          </HStack>
        </HStack>
        <HStack
          justify="space-between"
          overflow="hidden"
          py={2}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          <Text color="#667085" textStyle="body.regular.md">
            Details
          </Text>
          <Box borderRadius="md" px={2} py={1}>
            <Text
              fontWeight="bold"
              overflow="hidden"
              textAlign="center"
              textOverflow="ellipsis"
              textStyle="body.bold.sm"
              whiteSpace="nowrap"
            >
              {tokenPrice ? (
                <>
                  {`${labelNumber(tokenValue)} ${tokenLabel} 
              ($${labelNumber(tokenValue * tokenPrice)} USD)`}
                </>
              ) : (
                `${labelNumber(tokenValue)} ${tokenLabel}`
              )}
            </Text>
          </Box>
        </HStack>
      </Stack>
    </Stack>
  )
}

export default ProposalTransferReceipt
